import * as React from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { styled, useTheme } from '@mui/material/styles'
import { useNavigate, useLocation } from 'react-router-dom'
import { i18n } from '@safefleet/react-common'
import Icon from './ui/Icon'
import carsIcon from '../assets/img/cars.svg'
import deviceIcon from '../assets/img/device.svg'
import reportsIcon from '../assets/img/reports.svg'
import statisticIcon from '../assets/img/statistic.svg'
import highwayIcon from '../assets/img/highway.svg'
import { unfinishedFeatures } from '../utils'
import { trackPageView } from '../utils/analytics'
import { useSelector } from 'react-redux'
import { auth } from '@safefleet/react-common'
import axios from 'axios'
import instancesServer from '../utils/instancesServer'

// Sidebar width
const sidebarWidth = 240

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    "width"              : sidebarWidth,
    "flexShrink"         : 0,
    '& .MuiDrawer-paper' : {
        width           : sidebarWidth,
        boxSizing       : 'border-box',
        top             : theme.custom.appBar.height,
        height          : `calc(100% - ${theme.custom.appBar.height}px)`,
        borderRight     : '1px solid #EFEFEF',
        backgroundColor : '#F5F7FA', // Light gray-blue background color for sidebar
    },
}))

type SidebarButtonProps = {
    text: string,
    icon?: any,
    active?: boolean
    onClick?: () => any,
}

const SidebarButton: React.FC<SidebarButtonProps> = ({ text, icon, onClick, active }) => {
    const theme = useTheme()

    return (
        <Button
            fullWidth
            color="secondary"
            onClick={onClick}
            sx={{
                "flexDirection"   : "row",
                "justifyContent"  : "flex-start",
                "padding"         : 2,
                "height"          : '56px',
                "backgroundColor" : active ? '#E8EEF7' : '#F5F7FA',
                "borderRadius"    : 0,
                "borderLeft"      : active ? `4px solid ${theme.palette.primary.main}` : 'none',
                '&:hover'         : {
                    backgroundColor: '#E8EEF7',
                },
            }}
        >
            <Box sx={{
                marginRight : 2,
                display     : 'flex',
                alignItems  : 'center',
                color       : active ? theme.palette.primary.main : 'inherit',
            }}>
                {icon}
            </Box>
            <span style={{
                fontSize   : 14,
                fontWeight : active ? 'bold' : 'normal',
                color      : active ? theme.palette.primary.main : 'inherit',
            }}>
                {text}
            </span>
        </Button>
    )
}

const Sidebar: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const prefixUrl = window.location.hostname.split('.')[0]
    const authTokens: any = useSelector(auth.getJwtTokens)
    const accessToken = authTokens.accessToken
    const [jasperURL, setJasperURL] = React.useState('')
    const reportsEnabled = (jasperURL && ['electrica', 'alpha', 'staging'].includes(prefixUrl)) ? true : false
    const currentPath = location.pathname

    React.useEffect(() => {
        const serverUrl = instancesServer?.[prefixUrl]
        axios.get(
            `${serverUrl}/safeapi/api/reports/jasper-login-url`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        ).then((response) => {
            setJasperURL(response.data.login_url)
        })
    }, [accessToken, prefixUrl])

    const goHome = React.useCallback(() => {
        navigate('/')
    }, [navigate])

    const goJasper = () => {
        trackPageView('reports')
        window.open(jasperURL)
    }

    return (
        <StyledDrawer
            variant="permanent"
            anchor="left"
        >
            <Box sx={{ overflow: 'auto' }}>
                <List>
                    <ListItem disablePadding>
                        <SidebarButton
                            onClick={goHome}
                            active={currentPath === '/' || currentPath.startsWith('/')}
                            text={i18n.t("car fleet")}
                            icon={<Icon source={carsIcon} size={20} alt={"cars"} />}
                        />
                    </ListItem>
                    {reportsEnabled && (
                        <ListItem disablePadding>
                            <SidebarButton
                                text={i18n.t("reports")}
                                onClick={goJasper}
                                icon={<Icon source={reportsIcon} size={18} alt={"reports"} />}
                            />
                        </ListItem>
                    )}
                    {unfinishedFeatures && (
                        <>
                            <ListItem disablePadding>
                                <SidebarButton
                                    text={i18n.t("devices")}
                                    icon={<Icon source={deviceIcon} size={18} alt={"devices"} />}
                                />
                            </ListItem>
                            <ListItem disablePadding>
                                <SidebarButton
                                    text={i18n.t("statistics")}
                                    icon={<Icon source={statisticIcon} size={18} alt={"statistic"} />}
                                />
                            </ListItem>
                            <ListItem disablePadding>
                                <SidebarButton
                                    text={i18n.t("hu-go")}
                                    icon={<Icon source={highwayIcon} size={18} alt={"highway"} />}
                                />
                            </ListItem>
                        </>
                    )}
                </List>
                <Divider />
            </Box>
        </StyledDrawer>
    )
}

export default Sidebar